import React, { useEffect, useMemo, useState } from "react";
import {
  CommonButton,
  CommonFlexContainer,
  CommonHeaderPage,
  CommonSelect,
  CommonStyledPageContent,
  CommonTextCustomSize,
  CommonTextMedium,
} from "../../../../../components/common/AtomCommonComponents";
import { Col, Divider, Form, Row } from "antd";
import TextFormatUtils from "../../../../../utils/TextFormatUtils";
import { useHistory, useParams, useLocation } from "react-router-dom";
import moment from "moment";
import DialogUtils from "../../../../../utils/DialogUtils";
import CancelReasonModal from "../modals/CancelReasonModal";
import MedicaidService from "../../../../../services/api/MedicaidService";

interface ParamsType {
  id: string;
}

const ViewMedicaidDelivery = () => {
  const [orderData, setOrderData] = useState<any>({});
  const [orderSummaryList, setOrderSummaryList] = useState<any[]>([]);
  const [cancelReason, setCancelReason] = useState<string>("");
  const [isCancelReasonVisible, setIsCancelReasonVisible] =
    useState<boolean>(false);
  const [selectedStatus, setSelectedStatus] = useState<string>(
    orderData[0]?.order_status
  );

  const [form] = Form.useForm();

  const medicaidService = useMemo(() => new MedicaidService(), []);

  const history = useHistory();
  const location: any = useLocation();
  const params: ParamsType = useParams();

  const prevListState = location.state?.prevListState;

  const handleGoBack = () => {
    history.push({
      pathname: "/medicaid",
      state: prevListState, // pass it back
    });
  };

  const statusOptions = [
    {
      value: "pending",
      label: "Pending",
    },
    {
      value: "delivered",
      label: "Delivered",
    },
    {
      value: "cancelled",
      label: "Cancelled",
    },
  ];

  const onSelectStatus = async (
    value: string,
    option: { id: string; value: string }
  ) => {
    if (value === "cancelled") {
      setIsCancelReasonVisible(true);
    }
    setCancelReason("");
    setSelectedStatus(value);
  };

  const calculateDiscountAmount = (discount: any) => {
    if (discount.discount_type === "percentage") {
      let subTotal = orderData[0]?.product_total_amount;
      const priceDiscounted = subTotal * (discount.value / 100);
      return `- $${TextFormatUtils.formatNumberWithCommas(
        Math.round(priceDiscounted * 100) / 100,
        true
      )}`;
    } else {
      return ` - $${TextFormatUtils.formatNumberWithCommas(
        discount.value,
        true
      )}`;
    }
  };

  useEffect(() => {
    fetchOrder();
  }, []);

  const fetchOrder = async (): Promise<void> => {
    const result = await medicaidService.getOrder(params.id);
    setOrderSummaryList(result.order[0].order_items);
    setOrderData(result.order);
    setSelectedStatus(result.order[0]?.order_status);
  };

  const onSubmitReason = (reason: string) => {
    setCancelReason(reason);
    setIsCancelReasonVisible(false);
  };

  const onCancelReason = () => {
    setIsCancelReasonVisible(false);
    setSelectedStatus(orderData[0]?.order_status);
    setCancelReason("");
  };

  const customerName = orderData[0]?.customer?.full_name ?? "-";
  const customerEmail = orderData[0]?.customer?.email ?? "-";
  const orderNumber = orderData[0]?.transaction_no ?? "-";
  const totalOrder = orderData[0]?.overall_total_amount ?? "-";
  const subTotalOrder = orderData[0]?.product_total_amount ?? "-";
  const programs = Array.isArray(orderData[0]?.qualified_programs)
    ? orderData[0].qualified_programs.map((item: any) => item.name).join(", ")
    : typeof orderData[0]?.qualified_programs === "object" &&
      orderData[0]?.qualified_programs !== null
    ? orderData[0].qualified_programs.name || "-"
    : "-";
  const discounts = orderData[0]?.discounts ?? null;
  const deliveryNotes = orderData[0]?.delivery_notes ?? "-";
  const orderStatus = orderData[0]?.order_status
    ? TextFormatUtils.capitalizeFirstLetter(orderData[0]?.order_status)
    : "Status";
  const deliveryAddress = orderData[0]?.delivery_address
    ? TextFormatUtils.addressString(orderData[0]?.delivery_address)
    : "";
  let selectedBox = orderData[0]?.order_items[0].box_info ?? null;
  const totalAmount = orderData[0]?.overall_total_amount ?? "-";
  const boxName = `${orderData[0]?.order_items[0]?.box_info?.box_name}`;

  const calculateSubTotalPrice = (items: any) => {
    if (items == null) return 0;
    if (Array.isArray(items)) {
      return items.reduce((sum: number, item: any) => {
        if (!item) return sum;
        return sum + (item.total || 0);
      }, 0);
    }
    if (typeof items === "number") return items;
    if (typeof items === "string" && items.trim() !== "") {
      const num = Number(items);
      return isNaN(num) ? 0 : num;
    }
    return 0;
  };
  //date
  const dateMoment = (moment as any).tz
    ? (moment as any).tz(
        orderData[0]?.delivery_date_and_time ?? "",
        "America/New_York"
      )
    : moment(orderData[0]?.delivery_date_and_time ?? "");
  const dateString = dateMoment.format("YYYY-MM-DD");
  const timeString = dateMoment.format("hh:mm A");

  return (
    <>
      <CommonHeaderPage>
        <p>Medicaid Delivery Management</p>
      </CommonHeaderPage>
      <CommonStyledPageContent ishomedeliverymanagement>
        <CommonFlexContainer
          width="100%"
          spaceBetween
          alignCenter
          marginbottom="30px"
        >
          <CommonTextMedium fontWeight="900">
            TRANSACTION DETAILS: {orderNumber}
          </CommonTextMedium>
          {orderStatus === "Cancelled" ? (
            <CommonTextMedium fontWeight="900">Cancelled</CommonTextMedium>
          ) : (
            <CommonSelect
              width={"150px"}
              value={selectedStatus}
              options={statusOptions}
              disabled={true}
              onSelect={onSelectStatus as any}
            />
          )}
        </CommonFlexContainer>
        <Form form={form} validateTrigger="onChange">
          <Row gutter={[16, 16]}>
            <Col span={10}>
              <CommonFlexContainer column marginbottom="20px">
                <CommonTextCustomSize fontSize="14px" fontWeight="700">
                  CUSTOMER INFORMATION
                </CommonTextCustomSize>

                <Row>
                  <CommonTextCustomSize fontSize="14px">{`Name: \u00A0\u00A0${
                    customerName ? customerName : "\u00A0-"
                  }`}</CommonTextCustomSize>
                </Row>

                <Row>
                  <CommonTextCustomSize fontSize="14px">
                    {`Email Address: \u00A0\u00A0${
                      customerEmail ? customerEmail : "\u00A0-"
                    }`}
                  </CommonTextCustomSize>
                </Row>
              </CommonFlexContainer>
              <CommonFlexContainer column marginbottom="20px">
                <CommonTextCustomSize fontSize="14px" fontWeight="700">
                  PROGRAMS
                </CommonTextCustomSize>
                <Row>
                  <CommonTextCustomSize fontSize="14px">{`Programs: \u00A0\u00A0${
                    programs ? programs : "\u00A0-"
                  }`}</CommonTextCustomSize>
                </Row>
                <Row>
                  <CommonTextCustomSize fontSize="14px">{`Box Name: \u00A0\u00A0${
                    boxName ? boxName : "\u00A0-"
                  }`}</CommonTextCustomSize>
                </Row>
              </CommonFlexContainer>
              {orderData[0]?.allergen_data.removed_products.length > 0 && (
                <CommonFlexContainer column marginbottom="20px">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 12,
                      background: "#fff5f5",
                      border: "1px solid #ffcccc",
                      padding: 12,
                      borderRadius: 8,
                    }}
                  >
                    <div style={{ fontSize: 22, color: "#d32f2f", lineHeight: 1 }}>⚠️</div>
                    <div style={{ flex: 1 }}>
                      <CommonTextCustomSize
                        fontSize="14px"
                        fontWeight="700"
                        style={{ color: "#d32f2f", marginBottom: 8 }}
                      >
                        Changes Due to Allergens
                      </CommonTextCustomSize>

                        <div style={{ marginTop: 4, marginBottom: "-10px" }}>
                          <CommonTextCustomSize
                            fontSize="13px"
                            fontWeight="700"
                            style={{ display: "inline-block" }}
                          >
                            Removed Products:
                          </CommonTextCustomSize>
                          <CommonTextCustomSize
                            fontSize="13px"
                            style={{ wordBreak: "break-all", display: "inline-block", marginLeft: 8 }}
                          >
                            {Array.isArray(orderData[0]?.allergen_data?.removed_products) &&
                            orderData[0].allergen_data.removed_products.length
                              ? orderData[0].allergen_data.removed_products.map((p: any) => p.name).join(", ")
                              : "None"}
                          </CommonTextCustomSize>
                        </div>

                        <div style={{ marginTop: 1 }}>
                          <CommonTextCustomSize
                            fontSize="13px"
                            fontWeight="700"
                            style={{ display: "inline-block" }}
                          >
                            Customer Declared Allergen(s):
                          </CommonTextCustomSize>
                          <CommonTextCustomSize
                            fontSize="13px"
                            style={{ wordBreak: "break-all", display: "inline-block", marginLeft: 8 }}
                          >
                            {Array.isArray(orderData[0]?.allergen_data?.customer_allergens) &&
                            orderData[0].allergen_data.customer_allergens.length
                              ? orderData[0].allergen_data.customer_allergens.join(", ")
                              : "None"}
                          </CommonTextCustomSize>
                        </div>
                    </div>
                  </div>
                </CommonFlexContainer>
              )}
            </Col>
            {/* -------------------------------------2nd COL------------------------------------- */}
            <Col span={8}>
              <CommonTextCustomSize fontSize="14px" fontWeight="700">
                DELIVERY INFORMATION
              </CommonTextCustomSize>

              <Row style={{ gap: "37px" }}>
                <CommonTextCustomSize fontSize="14px">{`Date: \u00A0\u00A0${
                  dateString
                    ? moment(dateString).format("MM-DD-YYYY")
                    : "\u00A0-"
                }`}</CommonTextCustomSize>
                {orderStatus == "Delivered" || orderStatus == "Cancelled" ? (
                  <CommonTextCustomSize fontSize="14px">{`Time: \u00A0\u00A0${
                    timeString ? timeString : "\u00A0-"
                  }`}</CommonTextCustomSize>
                ) : (
                  <></>
                )}
              </Row>

              <Row>
                <CommonTextCustomSize fontSize="14px">{`Delivery Address:\u00A0\u00A0${deliveryAddress}`}</CommonTextCustomSize>
              </Row>

              <Row>
                <CommonTextCustomSize fontSize="14px">{`Notes: \u00A0\u00A0${
                  deliveryNotes ? deliveryNotes : "\u00A0-"
                }`}</CommonTextCustomSize>
              </Row>
              {cancelReason && (
                <>
                  <CommonTextCustomSize
                    fontSize="14px"
                    margintop="20px"
                    fontWeight="700"
                  >
                    REASON FOR CANCELLATION
                  </CommonTextCustomSize>
                  <Row>
                    <Col span={15} style={{ wordBreak: "break-all" }}>
                      <CommonTextCustomSize fontSize="14px">
                        {cancelReason}
                      </CommonTextCustomSize>
                    </Col>
                  </Row>
                </>
              )}

              {orderStatus === "Cancelled" && (
                <>
                  <CommonTextCustomSize
                    fontSize="14px"
                    margintop="20px"
                    fontWeight="700"
                  >
                    REASON FOR CANCELLATION
                  </CommonTextCustomSize>
                  <Row>
                    <Col span={15} style={{ wordBreak: "break-all" }}>
                      <CommonTextCustomSize fontSize="14px">
                        {orderData[0]?.reason_for_cancellation}
                      </CommonTextCustomSize>
                    </Col>
                  </Row>
                </>
              )}
            </Col>
          </Row>
          <Divider style={{ marginTop: "5px", width: "100%" }} />
          {selectedBox ? (
            <>
              {(() => {
                const products = selectedBox?.products || [];
                return (
                  <>
                    <CommonTextCustomSize
                      fontSize="14px"
                      fontWeight="700"
                      color="primary"
                    >
                      Selected Products
                    </CommonTextCustomSize>
                    {products.length ? (
                      <div
                        style={{
                          width: "500px",
                          border: "1px solid #e5e5e5",
                          borderRadius: 8,
                          padding: 12,
                          marginBottom: 20,
                          background: "#fafafa",
                        }}
                      >
                        <Row style={{ fontWeight: 600, marginBottom: 6 }}>
                          <Col span={12}>
                            <CommonTextCustomSize
                              fontSize="13px"
                              color="primary"
                            >
                              Product Name
                            </CommonTextCustomSize>
                          </Col>
                          <Col span={5} style={{ textAlign: "right" }}>
                            <CommonTextCustomSize
                              fontSize="13px"
                              color="primary"
                            >
                              Unit
                            </CommonTextCustomSize>
                          </Col>
                          <Col span={5} style={{ textAlign: "right" }}>
                            <CommonTextCustomSize
                              fontSize="13px"
                              color="primary"
                            >
                              Quantity
                            </CommonTextCustomSize>
                          </Col>
                        </Row>
                        {products.map((p: any, idx: number) => (
                          <Row
                            key={p.product_id || idx}
                            style={{
                              padding: "4px 0",
                              borderTop: idx === 0 ? "none" : "1px solid #eee",
                            }}
                          >
                            <Col span={12}>
                              <CommonTextCustomSize
                                fontSize="13px"
                                color="primary"
                              >
                                {p.name}
                              </CommonTextCustomSize>
                            </Col>
                            <Col span={5} style={{ textAlign: "right" }}>
                              <CommonTextCustomSize
                                fontSize="13px"
                                color="primary"
                              >
                                {p.unit}
                              </CommonTextCustomSize>
                            </Col>
                            <Col span={5} style={{ textAlign: "right" }}>
                              <CommonTextCustomSize
                                fontSize="13px"
                                color="primary"
                              >
                                {p.quantity}
                              </CommonTextCustomSize>
                            </Col>
                          </Row>
                        ))}
                      </div>
                    ) : (
                      <CommonTextCustomSize fontSize="13px" color="primary">
                        No products selected.
                      </CommonTextCustomSize>
                    )}
                  </>
                );
              })()}
              <Row style={{ width: "500px" }}>
                <Row style={{ width: "100%" }}>
                  <Col span={13}>
                    <CommonTextCustomSize
                      fontSize="14px"
                      fontWeight="700"
                      color="primary"
                    >
                      Subtotal:
                    </CommonTextCustomSize>
                  </Col>
                  <Col span={11}>
                    <CommonTextCustomSize
                      fontSize="14px"
                      fontWeight="700"
                      color="primary"
                    >
                      {"$" +
                        TextFormatUtils.formatNumberWithCommas(
                          calculateSubTotalPrice(
                            selectedBox ? totalAmount : "0"
                          ),
                          true
                        )}
                    </CommonTextCustomSize>
                  </Col>
                </Row>

                <Row style={{ width: "100%" }}>
                  <Col span={13}>
                    <CommonTextCustomSize
                      fontSize="14px"
                      fontWeight="700"
                      color="primary"
                    >
                      TOTAL AMOUNT:
                    </CommonTextCustomSize>
                  </Col>
                  <Col span={11}>
                    <CommonTextCustomSize
                      fontSize="16px"
                      fontWeight="700"
                      color="primary"
                    >
                      {"$" +
                        TextFormatUtils.formatNumberWithCommas(
                          calculateSubTotalPrice(
                            selectedBox ? totalAmount : "0"
                          ),
                          true
                        )}
                    </CommonTextCustomSize>
                  </Col>
                </Row>
              </Row>

              <CommonFlexContainer isJustifyRight gap="5px">
                <CommonButton key="cancel" onClick={handleGoBack}>
                  Go Back
                </CommonButton>
              </CommonFlexContainer>
            </>
          ) : null}
        </Form>
      </CommonStyledPageContent>
      <CancelReasonModal
        isCancelReasonModalVisible={isCancelReasonVisible}
        onSubmitReason={onSubmitReason}
        onCloseModal={onCancelReason}
      />
    </>
  );
};

export default ViewMedicaidDelivery;
